import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { format } from 'date-fns';
import {
  OvertimesToSum,
  OvertimeSummary,
  OvertimeTimeRangeKeys,
  PayrollCategoryName,
  Shift,
  ShiftState,
} from '@wilson/interfaces';
import {
  ResolvedShiftsState,
  ResolvedShiftWithSyncStatus,
} from '@wilson/shifts-mobile';
import { catchError, map, Observable, tap } from 'rxjs';
import { HomeStateService } from './home-state.service';
import {
  GetOvertimeStats,
  GetWorktimeStats,
  InitializeHomeStateShifts,
} from './home.action';
import { captureException } from '@sentry/browser';
import { ToastController } from '@ionic/angular/lazy';
import { TranslateService } from '@ngx-translate/core';
import { WorkTimeManagementDataService } from '@wilson/work-time-management';
import { getOvertimeDateRange } from '@wilson/utils';
import { DateTimeFormat } from '@wilson/utils';
import { YearMonth } from '@wilson/interfaces';

export const HomeStateName = 'home';

export interface HomeStateModel {
  shifts: Shift[];
  plannedWorkMinutes: number | null;
  workMinutes: number | null;
  overtimeInMinutes: number | null;
  worktimeLoading: boolean;
  overtimeLoading: boolean;
  overtimeDateRange: {
    startDate: Date;
    endDate: Date;
  };
}

@State<HomeStateModel>({
  name: HomeStateName,
  defaults: {
    shifts: [],
    plannedWorkMinutes: null,
    workMinutes: null,
    overtimeInMinutes: null,
    worktimeLoading: false,
    overtimeLoading: false,
    overtimeDateRange: {
      startDate: new Date(),
      endDate: new Date(),
    },
  },
})
@Injectable()
export class HomeState {
  private readonly homeStateService = inject(HomeStateService);
  private readonly shiftsState = inject(ResolvedShiftsState);
  private readonly toastController = inject(ToastController);
  private readonly translateService = inject(TranslateService);
  private readonly workTimeDataService = inject(WorkTimeManagementDataService);

  readonly syncingShiftsData$ = this.shiftsState.syncingShiftsData$;
  readonly syncing$ = this.syncingShiftsData$;

  @Selector()
  static unsubmittedShifts(state: HomeStateModel): Shift[] {
    return (state.shifts || []).filter(
      (shift) => shift.state === ShiftState.NotSubmitted,
    );
  }
  @Selector()
  static reopenedShifts(state: HomeStateModel): Shift[] {
    return (state.shifts || []).filter(
      (shift) => shift.state === ShiftState.Reopened,
    );
  }

  @Selector()
  static isEmptyShifts(state: HomeStateModel): boolean {
    return !state.shifts?.length;
  }
  @Selector()
  static isEmptyUnsubmittedShifts(state: HomeStateModel): boolean {
    return !this.unsubmittedShifts(state).length;
  }
  @Selector()
  static isEmptyReopenedShifts(state: HomeStateModel): boolean {
    return !this.reopenedShifts(state).length;
  }
  @Selector()
  static plannedWorkMinutes(state: HomeStateModel): number | null {
    return state.plannedWorkMinutes;
  }

  @Selector()
  static workMinutes(state: HomeStateModel): number | null {
    return state.workMinutes;
  }

  @Selector()
  static overtimeDateRange(state: HomeStateModel): {
    startDate: Date;
    endDate: Date;
  } {
    return state.overtimeDateRange;
  }
  @Selector()
  static overtimeMinutes(state: HomeStateModel): number | null {
    return state.overtimeInMinutes;
  }

  @Selector()
  static worktimeLoading(state: HomeStateModel): boolean {
    return state.worktimeLoading;
  }

  @Selector()
  static overtimeLoading(state: HomeStateModel): boolean {
    return state.overtimeLoading;
  }

  @Action(InitializeHomeStateShifts, { cancelUncompleted: true })
  initializeHomeStateShifts(
    context: StateContext<HomeStateModel>,
  ): Observable<ResolvedShiftWithSyncStatus[]> {
    return this.homeStateService.getShifts().pipe(
      tap({
        next: (shifts) => {
          context.patchState({ shifts });
        },
      }),
    );
  }

  @Action(GetWorktimeStats)
  initializeWorkTimeStats(
    context: StateContext<HomeStateModel>,
  ): Observable<Record<PayrollCategoryName, number> | undefined> {
    const now = new Date();
    const formatted = format(now, DateTimeFormat.MonthFormat) as YearMonth;

    context.patchState({ worktimeLoading: true });
    return this.workTimeDataService
      .getWorktimeSummary(
        formatted,
        [PayrollCategoryName.IsWorkTime, PayrollCategoryName.PlannedWorktime],
        true,
      )
      .pipe(
        tap((result) => {
          context.patchState({
            plannedWorkMinutes: result[PayrollCategoryName.PlannedWorktime],
            workMinutes: result[PayrollCategoryName.IsWorkTime],
            worktimeLoading: false,
          });
        }),
        catchError((e) => {
          context.patchState({ worktimeLoading: false });
          return this.handleError(e);
        }),
      );
  }

  @Action(GetOvertimeStats)
  initializeOvertimeStats(
    context: StateContext<HomeStateModel>,
    { userId }: GetOvertimeStats,
  ): Observable<
    Partial<Record<OvertimeTimeRangeKeys, OvertimeSummary>> | undefined
  > {
    context.patchState({ overtimeLoading: true });

    const { startDate, endDate } = getOvertimeDateRange(new Date());
    context.patchState({ overtimeDateRange: { startDate, endDate } });

    return this.workTimeDataService
      .getOvertimeSummaryByRanges(userId, {
        [OvertimeTimeRangeKeys.SelectedYear]: {
          timeRange: {
            start: startDate,
            end: endDate,
          },
          fields: [OvertimesToSum.OvertimeInMinutes],
        },
      })
      .pipe(
        tap((overtimeData) =>
          context.patchState({
            overtimeInMinutes:
              overtimeData.selectedYear?.[OvertimesToSum.OvertimeInMinutes] ??
              0,
            overtimeLoading: false,
          }),
        ),
        catchError((e) => {
          context.patchState({ overtimeLoading: false });
          return this.handleError(e);
        }),
      );
  }

  private async handleError(e: Error): Promise<undefined> {
    captureException(e);
    const toast = await this.toastController.create({
      color: 'danger',
      message: this.translateService.instant('general.errorMessage'),
      duration: 3000,
      position: 'top',
    });
    await toast.present();
  }
}
